// ConductRules: the rules Otto works under, each saying who enforces it.
// "code" rules are checked by the guard (packages/guard) and never trusted to
// the prompt alone; "prompt" rules go into Otto's brief; "both" does both
// (design 5.1: the voice model is never the only thing enforcing anything
// that matters).
import { z } from "zod";
import { DraftLabel, Id, behaviorText, requireUnique } from "./common";
import { GuardLists } from "./guard-lists";

export const EnforcedBy = z.enum(["code", "prompt", "both"]);
export type EnforcedBy = z.infer<typeof EnforcedBy>;

export const ConductRule = z.object({
  id: Id,
  text: behaviorText(2000),
  enforced_by: EnforcedBy,
  /** A breach of a hard gate fails the consult's quality check (design 10.1). */
  hard_gate: z.boolean().default(false),
  /** For code or both: the guard check that enforces it. The quality engineer names these. */
  check_id: Id.optional(),
  /**
   * Where the rule comes from, for Otto Studio: a Character Guide page, a
   * decision and its date, or "PROPOSAL, Wes to confirm". Not sent to Otto.
   */
  source: z.string().min(1).max(200).optional(),
});
export type ConductRule = z.infer<typeof ConductRule>;

export const ConductRules = z
  .object({
    draft_label: DraftLabel,
    rules: z.array(ConductRule).min(1).max(200),
    /**
     * The output guard's word lists, versioned with the rules that forbid the
     * words. Absent: the guard cannot run, which the bundle loader warns
     * about and otto-app treats as "guard unavailable" (loud, nothing
     * approved on its word).
     */
    guard_lists: GuardLists.optional(),
  })
  .superRefine((doc, ctx) => {
    requireUnique(
      doc.rules.map((r) => r.id),
      ctx,
      ["rules"],
      "rule id",
    );
  });
export type ConductRules = z.infer<typeof ConductRules>;

/** Rules whose text goes into Otto's brief. */
export function promptRules(doc: ConductRules): ConductRule[] {
  return doc.rules.filter((r) => r.enforced_by !== "code");
}

/** Rules the guard must enforce in code. */
export function codeRules(doc: ConductRules): ConductRule[] {
  return doc.rules.filter((r) => r.enforced_by !== "prompt");
}
